import React, { FC, useState } from 'react';
import { T } from 'src/shared/helpers';
import { RelayFragment } from 'src/shared/types';
import styled from 'styled-components';
import { merchantProduct_item$data } from '../../state/queries/__generated__/merchantProduct_item.graphql';
import StoreCatalog from '../../storeCatalog/storeCatalog';
import { Dialog, DialogWindow, QuestionDialog, useDialogManager } from '../../../../widgets/dialog';

const CustomDialogWindow = styled(DialogWindow)`
    flex: 1;
    align-self: stretch;
    justify-self: stretch;
    margin: 0 80px;
`;

const DuplicateDialog: FC<{
    sourceProductCode: string;
    salesChannelID: string;
    onConfirm: (skus: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>[]) => void;
}> = ({ sourceProductCode, salesChannelID, onConfirm }) => {
    const [selectedProducts, setSelectedProducts] = useState<Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>[]>([]);
    const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>();
    const { showDialog, closeDialog } = useDialogManager();

    const handleProductClick = async (merchantProduct: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>) => {
        if (selectedProducts.findIndex((x) => x.sku === merchantProduct.sku!) === -1) {
            setSelectedProducts([...selectedProducts, merchantProduct]);
        } else {
            setSelectedProducts(selectedProducts.filter((x) => x.sku !== merchantProduct.sku));
        }
    };

    const handleDuplicateClick = () => {
        if (selectedProducts.some((product) => product.isConfiguratedInZakeke))
            showDialog(
                'duplication-confirm',
                <QuestionDialog
                    message={
                        <span>
                            {T._(
                                'ATTENTION: you have selected one or more products already configured as customizable. If you proceed, the new personalization settings will overwrite the existing ones. Are you sure to proceed with all the selected products?',
                                'Admin'
                            )}
                        </span>
                    }
                    onYesClick={() => {
                        closeDialog('duplication-confirm');
                        onConfirm(selectedProducts);
                    }}
                    onNoClick={() => {
                        const filteredProducts = selectedProducts.filter((product) => !product.isConfiguratedInZakeke);
                        closeDialog('duplication-confirm');
                        onConfirm(filteredProducts);
                    }}
                    buttonYesLabel={T._('YES, duplicate on all selected products.', 'Admin')}
                    buttonNoLabel={T._('NO, duplicate only on not configured products.', 'Admin')}
                />
            );
        else onConfirm(selectedProducts);
    };

    return (
        <Dialog
            windowDecorator={CustomDialogWindow}
            buttons={[{ label: T._('Duplicate', 'Admin'), onClick: handleDuplicateClick }]}
            ref={(instance) => setScrollContainer(instance)}
        >
            <StoreCatalog
                cardTitle={T._('Select the products you want to duplicate on', 'Admin')}
                salesChannelID={salesChannelID}
                onProductClick={handleProductClick}
                scrollContainer={scrollContainer}
                selectedProductsSKUs={selectedProducts.map((x) => x.sku!)}
                showHeader={false}
                showBackButton={false}
                disableProductCode={sourceProductCode}
            />
        </Dialog>
    );
};

export default DuplicateDialog;
